import type { CSSProperties, ComponentPropsWithRef, MouseEvent } from "react";
import { forwardRef, useCallback } from "react";
import { Column, Link, Row, Section } from "@react-email/components";
import type { StyleValue } from "@circle-react-shared/BuilderUI/StyleValue.types";
import { TextRenderer } from "@circle-react-shared/Emails/VisualEmailBuilder/EditorFields/RichTextField/TextRenderer";
import { useParseStyles } from "../../../Design/useParseStyles";
import type { AlignmentOption } from "../../../ModifiedStyleAttributes/objectAlignment";
import type { MarginValues } from "../../../Utils/buttonStyleHelpers";
import { convertMarginToPadding } from "../../../Utils/buttonStyleHelpers";
import { Mso, useMsoStyles } from "../../../Utils/useMsoStyles";
import { defaultStyles } from "./defaultStyles";

interface ButtonProps extends ComponentPropsWithRef<"table"> {
  blockPath: string;
  "data-preview-mode": boolean;
  href: string;
  styles: Record<string, StyleValue>;
  text: string;
}

export const SecondaryButton = forwardRef<HTMLAnchorElement, ButtonProps>(
  (
    {
      text,
      href,
      styles,
      blockPath,
      "data-preview-mode": isPreviewMode,
      ...rest
    },
    ref,
  ) => {
    const { alignment: styledAlignment, margin, ...otherStyles } = styles;
    const { margin: defaultMargin, ...otherDefaultStyles } = defaultStyles;

    // this is a workaround to allow margin to be used as padding for the button section
    const sectionPadding = {
      type: margin.type,
      value: convertMarginToPadding(margin.value as MarginValues),
    };
    const sectionDefaultPadding = convertMarginToPadding(
      defaultMargin as MarginValues,
    );

    const alignment = (styledAlignment?.value ||
      defaultStyles.alignment) as AlignmentOption;

    // NOTE: margin is ignored by Outlook at the moment
    const tableStyles: CSSProperties = useParseStyles({
      baseStyles: {},
      styles: { padding: sectionPadding },
      defaultStyles: { padding: sectionDefaultPadding },
    });

    const linkStyles: CSSProperties = useParseStyles({
      baseStyles: {
        display: "inline-block",
        borderStyle: "solid",
        textDecoration: "none",
        textAlign: "center",
      },
      styles: otherStyles,
      defaultStyles: otherDefaultStyles,
    });

    // MSO styles for Outlook compatibility - harmless on other clients
    const { msoPadding, msoBg, msoBorderColor, msoBorderWidth } = useMsoStyles({
      linkStyles,
    });
    (linkStyles as Record<string, unknown>)["mso-padding-alt"] = msoPadding;

    const handleClick = useCallback(
      (event: MouseEvent<HTMLAnchorElement>) => {
        if (isPreviewMode) event.preventDefault();
      },
      [isPreviewMode],
    );

    const defaultSectionProps = {
      align: "center",
      width: "100%",
    };

    return (
      <Section style={tableStyles} {...defaultSectionProps} {...rest}>
        <Row>
          <Column align={alignment}>
            <Mso
              html={`<!--[if mso]><table role="presentation" border="0" cellpadding="0" cellspacing="0" align="${alignment}"><tr><td style="background:${msoBg}; background-color:${msoBg}; padding:${msoPadding}; border:${msoBorderWidth} solid ${msoBorderColor};"><![endif]-->`}
            />

            <Link
              ref={ref}
              href={href}
              onClick={handleClick}
              rel="noopener"
              style={linkStyles}
            >
              <TextRenderer blockPath={blockPath} text={text} />
            </Link>

            <Mso html="<!--[if mso]></td></tr></table><![endif]-->" />
          </Column>
        </Row>
      </Section>
    );
  },
);

SecondaryButton.displayName = "SecondaryButton";
